import { useHoveredItemActions } from 'components/WorkspaceEditor/HoveredItemStore'
import { useCallback, useState } from 'react'
import { useBestPracticesQueries } from './useBestPracticesQueries'

// Global ref to track if ANY BP menu is open (shared across all component instances)
// Prevents multiple menus from opening simultaneously
const globalMenuOpenRef = { current: false }

/**
 * Parameters for opening the Best Practices menu
 */
export interface BPMenuOpenParams {
    /** The anchor element for the popover */
    anchorElement: Element
    /** The content item ID (parent for pills, node.key for articles/paragraphs) */
    contentItemId: string
    /** The content item type */
    contentItemType: string
    /** The element ID (only for pills) */
    elementId?: string
    /** The project ID */
    projectId: string
    /**Parent paragraph id for current option group  */
    parentOptionGroupId?: string
}

/**
 * Custom hook for managing Best Practices context menu functionality.
 *
 * Extracts shared BP menu logic used by both NodeTitle and SelectionOption components:
 * - State management for menu open/close and anchor element
 * - GraphQL queries for fetching BP data
 * - Store actions for setting selected nodes/elements
 * - Handlers for opening and closing the menu
 *
 * @returns Object with menu state, handlers, and query functions
 *
 * @example
 * const { isBPMenuOpen, bpAnchorElement, openBPMenu, closeBPMenu } = useBestPracticesMenu()
 *
 * const handleRightClick = (event) => {
 *   openBPMenu({
 *     anchorElement: event.currentTarget,
 *     contentItemId: node.key,
 *     contentItemType: node.type,
 *     projectId: projectId
 *   })
 * }
 */
export const useBestPracticesMenu = () => {
    // Menu state
    const [bpAnchorElement, setBpAnchorElement] = useState<Element | null>(null)
    const [isBPMenuOpen, setIsBPMenuOpen] = useState(false)

    // Store actions
    const {
        setSelectedMainMenuNode,
        setSelectedElementId,
    } = useHoveredItemActions()

    // GraphQL queries (shared hook)
    const {
        getBestPracticeElementData,
        getProjectData,
    } = useBestPracticesQueries()

    /**
     * Opens the Best Practices menu and fetches necessary data.
     * Handles all state updates and data fetching internally.
     */
    const openBPMenu = useCallback(
        ({
            anchorElement,
            contentItemId,
            contentItemType,
            elementId,
            projectId,
            parentOptionGroupId,
        }: BPMenuOpenParams) => {
            // Prevent opening if another menu is already open globally
            if (globalMenuOpenRef.current) {
                return
            }

            globalMenuOpenRef.current = true
            setBpAnchorElement(anchorElement)
            setIsBPMenuOpen(true)

            // Set the selected node for mutation
            setSelectedMainMenuNode({
                id: parentOptionGroupId || contentItemId,
                type: contentItemType,
            })

            // Set element ID (only for pills, undefined for articles/paragraphs)
            setSelectedElementId(elementId)

            // Fetch BP data
            getBestPracticeElementData({
                variables: {
                    parentId: elementId || contentItemId,
                    projectId: projectId,
                },
            })

            getProjectData({
                variables: {
                    projectId: projectId,
                },
            })
        },
        [
            getBestPracticeElementData,
            getProjectData,
            setSelectedMainMenuNode,
            setSelectedElementId,
        ]
    )

    /**
     * Closes the Best Practices menu and clears state
     */
    const closeBPMenu = useCallback(() => {
        globalMenuOpenRef.current = false
        setBpAnchorElement(null)
        setIsBPMenuOpen(false)
        setSelectedElementId(undefined)
    }, [setSelectedElementId])

    return {
        // State
        isBPMenuOpen,
        bpAnchorElement,
        // Handlers
        openBPMenu,
        closeBPMenu,
    }
}
